import {
  ContractsApi,
  FactsLakeApi,
  HashingApi,
  ProvidersRunnerApi,
  TechInsightsApi,
  TechInsightsApiConfig,
  TechStackApi,
  WorkflowsApi,
} from '@mercedes-benz/tech-insights-common';
import { ApiFactory } from './ApiFactory';
import { Hashing } from './Hashing';

/**
 * TechInsightsClient is a client for interacting with the Tech Insights APIs.
 * It provides methods to access various APIs such as Contracts, Facts Lake, Tech Stack, and Providers Runner.
 *
 * @template SecurityDataType - The type of security data used in the API requests.
 */
export class TechInsightsClient<
  SecurityDataType extends unknown,
> implements TechInsightsApi<SecurityDataType> {
  private contractsApi?: ContractsApi<SecurityDataType>;
  private workflowsApi?: WorkflowsApi<SecurityDataType>;
  private factsLakeApi?: FactsLakeApi<SecurityDataType>;
  private techStackApi?: TechStackApi<SecurityDataType>;
  private providersRunnerApi?: ProvidersRunnerApi<SecurityDataType>;
  private hashingApi?: HashingApi;

  private constructor(private readonly config: TechInsightsApiConfig) {}

  static create<SecurityDataType = unknown>(
    config: TechInsightsApiConfig,
  ): TechInsightsApi<SecurityDataType> {
    return new TechInsightsClient(config);
  }

  contracts(): ContractsApi<SecurityDataType> {
    if (!this.contractsApi) {
      this.contractsApi = ApiFactory.createContractsApi(this.config);
    }
    return this.contractsApi;
  }

  workflows(): WorkflowsApi<SecurityDataType> {
    if (!this.workflowsApi) {
      this.workflowsApi = ApiFactory.createWorkflowsApi(this.config);
    }
    return this.workflowsApi;
  }

  factsLake(): FactsLakeApi<SecurityDataType> {
    if (!this.factsLakeApi) {
      this.factsLakeApi = ApiFactory.createFactsLakeApi(this.config);
    }
    return this.factsLakeApi;
  }

  techStack(): TechStackApi<SecurityDataType> {
    if (!this.techStackApi) {
      this.techStackApi = ApiFactory.createTechStackApi(this.config);
    }
    return this.techStackApi;
  }

  providersRunner(): ProvidersRunnerApi<SecurityDataType> {
    if (!this.providersRunnerApi) {
      this.providersRunnerApi = ApiFactory.createProvidersRunnerApi(
        this.config,
      );
    }
    return this.providersRunnerApi;
  }

  hashing(): HashingApi {
    if (!this.hashingApi) {
      this.hashingApi = new Hashing();
    }
    return this.hashingApi;
  }
}
